import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, TrendingUp, Heart, Package, Users, Sparkles, CheckCircle2, Compass } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { API } from "@/App";

const HomePage = () => {
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const catRes = await axios.get(`${API}/categories`);
        setCategories(catRes.data);
      } catch (e) {
        console.error("Error fetching data:", e);
      }
    };
    fetchData();
  }, []);

  const services = [
    {
      icon: Compass,
      title: "Piloter sa stratégie",
      description: "Transformez votre modèle économique et élaborez votre plan de transition vers une entreprise responsable.",
      link: "/piloter-strategie",
      color: "#1D3557",
      span: "md:col-span-5 lg:col-span-7",
    },
    {
      icon: Heart,
      title: "Fidéliser ses clients",
      description: "Programmes de fidélité innovants et cumul d'achats pour créer une relation durable.",
      link: "/fideliser-clients",
      color: "#1D3557",
      span: "md:col-span-3 lg:col-span-5",
    },
    {
      icon: TrendingUp,
      title: "Développer ses ventes",
      description: "Jeux concours, instants gagnants et offres de remboursement pour booster votre chiffre d'affaires.",
      link: "/developper-ventes",
      color: "#8C9D78",
      span: "md:col-span-4 lg:col-span-6",
    },
    {
      icon: Package,
      title: "Valoriser sa marque",
      description: "Objets publicitaires personnalisés et éco-responsables pour votre communication.",
      link: "/boutique",
      color: "#8C9D78",
      span: "md:col-span-4 lg:col-span-6",
    },
  ];

  const values = [
    { title: "Engagement", description: "Marketing responsable et durable" },
    { title: "Expertise", description: "15 ans d'expérience en marketing" },
    { title: "Proximité", description: "Basée en Corse, active en France" },
    { title: "Innovation", description: "Solutions créatives et modernes" },
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative hero-gradient overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 noise-bg"></div>
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[85vh] py-20">
            {/* Left Content */}
            <div className="space-y-8 animate-fade-in-up">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#8C9D78]/20 text-[#8C9D78]">
                <Sparkles className="w-4 h-4" />
                <span className="text-sm font-medium">Agence de marketing engagé</span>
              </div>
              
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-[#1D3557] leading-tight" style={{ fontFamily: 'Fraunces, serif' }}>
                Il est temps de prendre{" "}
                <span className="text-[#8C9D78]">au sérieux</span>{" "}
                nos rêves
              </h1>
              
              <p className="text-lg md:text-xl text-stone-600 max-w-xl leading-relaxed">
                Et d'en faire une <strong>stratégie</strong>. MOREATI accompagne les entreprises 
                de Corse et de France dans leur transformation vers un marketing engagé et responsable.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button asChild className="bg-[#8C9D78] hover:bg-[#7A8B69] text-white rounded-full px-8 h-14 text-lg font-semibold shadow-lg" data-testid="cta-discover">
                  <Link to="/boutique">
                    Découvrir nos solutions
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Link>
                </Button>
                <Button asChild variant="outline" className="btn-secondary h-14 text-lg" data-testid="cta-contact">
                  <Link to="/contact">Nous contacter</Link>
                </Button>
              </div>

              <div className="flex items-center gap-8 pt-4">
                <div>
                  <p className="text-3xl font-bold text-[#1D3557]">15+</p>
                  <p className="text-sm text-stone-500">Années d'expérience</p>
                </div>
                <div className="w-px h-12 bg-stone-300"></div>
                <div>
                  <p className="text-3xl font-bold text-[#8C9D78]">100+</p>
                  <p className="text-sm text-stone-500">Clients accompagnés</p>
                </div>
                <div className="w-px h-12 bg-stone-300"></div>
                <div>
                  <p className="text-3xl font-bold text-[#1D3557]">Corse</p>
                  <p className="text-sm text-stone-500">& France entière</p>
                </div>
              </div>
            </div>

            {/* Right Visual */}
            <div className="relative hidden lg:block">
              <div className="relative">
                <div className="absolute -top-10 -right-10 w-72 h-72 bg-[#C65D47]/10 rounded-full blur-3xl animate-pulse-soft"></div>
                <div className="absolute -bottom-10 -left-10 w-72 h-72 bg-[#8C9D78]/10 rounded-full blur-3xl animate-pulse-soft" style={{ animationDelay: "1s" }}></div>
                
                <img
                  src="https://images.unsplash.com/photo-1587032286160-e6b508ed7226?w=800&h=600&fit=crop"
                  alt="Paysage Corse"
                  className="relative z-10 rounded-3xl shadow-2xl"
                />
                
                {/* Floating Card */}
                <div className="absolute -bottom-6 -left-6 z-20 glass rounded-2xl p-6 animate-float">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-[#8C9D78] flex items-center justify-center">
                      <CheckCircle2 className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-[#1D3557]">Marketing Engagé</p>
                      <p className="text-sm text-stone-500">Certifié RSE</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Wave Divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="#FDFBF7"/>
          </svg>
        </div>
      </section>

      {/* Services Section - Bento Grid */}
      <section className="section-padding bg-[#FDFBF7]" data-testid="services-section">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-sm font-semibold text-[#C65D47] uppercase tracking-widest">Nos Expertises</span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#1D3557] mt-4 mb-6" style={{ fontFamily: 'Fraunces, serif' }}>
              Agence de marketing engagé
            </h2>
            <p className="text-lg text-stone-600">
              Nous vous accompagnons à développer votre vision stratégique durable et responsable
            </p>
          </div>

          <div className="bento-grid">
            {services.map((service, index) => (
              <Link
                key={index}
                to={service.link}
                className={`card-service group ${service.span} min-h-[280px] flex flex-col justify-between transition-all duration-500 hover:shadow-xl`}
                style={{ "--hover-bg": service.color }}
                data-testid={`service-card-${index}`}
              >
                <div className="relative z-10">
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6 transition-all duration-500 group-hover:scale-110 group-hover:bg-white/20"
                    style={{ backgroundColor: `${service.color}15` }}
                  >
                    <service.icon className="w-7 h-7 transition-colors duration-500 service-icon" style={{ "--icon-color": service.color, color: "var(--icon-color)" }} />
                  </div>
                  <h3 className="text-2xl font-semibold text-[#1D3557] mb-3 transition-colors duration-500 group-hover:text-white" style={{ fontFamily: 'Fraunces, serif' }}>
                    {service.title}
                  </h3>
                  <p className="text-stone-600 leading-relaxed transition-colors duration-500 group-hover:text-white/80">{service.description}</p>
                </div>
                <div className="flex items-center gap-2 text-[#C65D47] font-medium mt-6 group-hover:gap-4 group-hover:text-white transition-all duration-500 relative z-10">
                  En savoir plus
                  <ArrowRight className="w-4 h-4" />
                </div>
                <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ backgroundColor: service.color }} />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* About Maud Section */}
      <section className="section-padding bg-[#F4F1EA]" data-testid="about-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-full h-full bg-[#8C9D78]/20 rounded-3xl"></div>
              <img
                src="https://customer-assets.emergentagent.com/job_moreati-preview/artifacts/0v4yr7f4_%40PARLACOM-MAUD-51.jpg"
                alt="Maud Dupupet - MOREATI"
                className="relative z-10 rounded-3xl shadow-xl w-full object-cover"
                style={{ maxHeight: "600px" }}
              />
            </div>

            <div className="space-y-8">
              <span className="text-sm font-semibold text-[#8C9D78] uppercase tracking-widest">À Propos</span>
              <h2 className="text-4xl md:text-5xl font-bold text-[#1D3557]" style={{ fontFamily: 'Fraunces, serif' }}>
                Maud Dupupet,<br />
                <span className="text-[#8C9D78]">votre partenaire engagée</span>
              </h2>
              <p className="text-lg text-stone-600 leading-relaxed">
                Basée à Ajaccio en Corse, MOREATI est née de la conviction que le marketing 
                peut être un levier de transformation positive. Avec plus de 15 ans d'expérience, 
                nous accompagnons les entreprises dans leur démarche RSE et leur communication responsable.
              </p>

              <div className="grid grid-cols-2 gap-6">
                {values.map((value, index) => (
                  <div key={index} className="p-4 bg-white rounded-xl border-l-4 border-[#8C9D78]">
                    <h4 className="font-semibold text-[#1D3557] mb-1">{value.title}</h4>
                    <p className="text-sm text-stone-500">{value.description}</p>
                  </div>
                ))}
              </div>

              <Button asChild className="bg-[#8C9D78] hover:bg-[#7A8B69] text-white rounded-full px-8 py-4 font-semibold shadow-lg">
                <Link to="/contact">
                  Discutons de votre projet
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Preview */}
      {categories.length > 0 && (
        <section className="section-padding" data-testid="categories-section">
          <div className="container-custom">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 mb-12">
              <div>
                <span className="text-sm font-semibold text-[#C65D47] uppercase tracking-widest">Boutique</span>
                <h2 className="text-4xl font-bold text-[#1D3557] mt-4" style={{ fontFamily: 'Fraunces, serif' }}>
                  Valoriser sa marque
                </h2>
              </div>
              <Button asChild variant="outline" className="btn-secondary">
                <Link to="/boutique">
                  Voir tous les produits
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {categories.map((category) => (
                <Link
                  key={category.id}
                  to={`/boutique?category=${category.id}`}
                  className="group relative aspect-[4/5] rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
                  data-testid={`category-${category.slug}`}
                >
                  <img
                    src={category.image_url}
                    alt={category.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1D3557] via-[#1D3557]/30 to-transparent opacity-70 group-hover:opacity-90 transition-opacity duration-500"></div>
                  <div className="absolute inset-0 border-2 border-transparent group-hover:border-white/30 rounded-2xl transition-all duration-500" />
                  <div className="absolute bottom-0 left-0 right-0 p-5 translate-y-2 group-hover:translate-y-0 transition-transform duration-500">
                    <h3 className="text-white font-bold text-xl mb-1">{category.name}</h3>
                    <p className="text-white/0 group-hover:text-white/70 text-sm transition-all duration-500 transform">
                      Découvrir la collection
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-[#8C9D78] relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-[#1D3557] rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#C65D47] rounded-full blur-3xl"></div>
        </div>
        <div className="container-custom relative z-10 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Fraunces, serif' }}>
            Prêt à transformer votre marketing ?
          </h2>
          <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto">
            Contactez-nous pour discuter de votre projet et découvrir comment nous pouvons vous accompagner.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild className="bg-white text-[#8C9D78] hover:bg-stone-100 rounded-full px-10 h-14 text-lg font-semibold">
              <Link to="/contact" data-testid="cta-final-contact">
                Demander un devis gratuit
              </Link>
            </Button>
            <Button asChild variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#8C9D78] rounded-full px-10 h-14 text-lg">
              <a href="tel:+33751131092">
                07 51 13 10 92
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
